import { Field } from 'factorial-form'
import each from 'lodash/each'
import merge from 'lodash/merge'
import reduce from 'lodash/reduce'
import { Moment } from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'

import { DaysTree } from 'modules/timeoff/lib/getDateRangeFromTree'

import { useEnableDay } from './lib/useEnableDay'

type DateValues = [string | undefined, string | undefined]

export interface Props {
  startDate: Field
  endDate: Field
  minDate?: Moment | null | undefined
  readonly: boolean
  holidays: DaysTree
  blockedPeriods: DaysTree
  existingLeaves: DaysTree
  withNull?: boolean
  bulkAdd?: boolean
  isHourlyRequest?: boolean
}

const DateRangePicker = ({
  startDate,
  endDate,
  minDate,
  readonly,
  holidays,
  blockedPeriods,
  existingLeaves,
  withNull,
  bulkAdd,
  isHourlyRequest,
}: Props) => {
  const { access } = useCurrentSession()
  const firstDayOfWeek = firstDayOfWeekAsIndex(access.firstDayOfWeek)
  const onChangeEvent = (values: DateValues) => {
    const [startValue, endValue] = values

    const start = startValue ? dateBackendToUI(startValue) : undefined
    const end = endValue ? dateBackendToUI(endValue) : undefined

    startDate.set(start)
    endDate.set(end)
  }

  const daysToDisable = merge({}, blockedPeriods, existingLeaves)

  const enableDay = useEnableDay({
    blockedPeriods,
    existingLeaves,
    bulkAdd,
    isHourlyRequest,
    daysToDisable,
  })

  const daysTreeToDateArray = (
    acc: Date[],
    monthly: Record<number, Record<number, string[]>>,
    year: string
  ) => {
    each(monthly, (days: Record<number, string[]>, month: string) => {
      each(days, (_, day: string) => {
        acc.push(new Date(Number(year), Number(month) - 1, Number(day)))
      })
    })

    return acc
  }

  const allBlockedData = { ...blockedPeriods, ...existingLeaves }
  const blockedDays = reduce(allBlockedData, daysTreeToDateArray, [])

  return (
    <DayPicker
      mode="range"
      format={'ui_dmy_long'}
      label={i18n.t('time_off.date_range.label')}
      readonly={readonly}
      minDate={minDate}
      holidays={holidays}
      enableDay={enableDay}
      blockedDays={blockedDays}
      onChange={onChangeEvent}
      value={[
        startDate.value ? dateUIToBackend(startDate.value) : undefined,
        endDate.value ? dateUIToBackend(endDate.value) : undefined,
      ]}
      firstDayOfWeek={firstDayOfWeek}
      focusTrapActive={false}
      withNull={withNull}
      withNullLabel={i18n.t('time_off.date_range.unknown_end_date.label')}
      withNullButtonLabel={i18n.t('time_off.date_range.unknown_end_date.button')}
    />
  )
}

export default observer(DateRangePicker)
